import { useState } from "react";
import { createFileRoute, useNavigate, useParams } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { ApiError } from "@njbsoft/api-client";
import { mapApiError } from "@njbsoft/i18n";
import { type TaskCreate, type TaskUpdate, useTask, useUpdateTask } from "@/hooks/use-tasks";
import { useToast } from "@/hooks/use-toast";
import { PageHeader, Breadcrumbs, PageSkeleton, EmptyState } from "@/components/ui";
import { TaskForm } from "@/components/tasks/task-form";

export const Route = createFileRoute("/_authenticated/tasks/$id/edit")({
  component: EditTaskPage,
});

function EditTaskPage() {
  const { t } = useTranslation(["tasks", "common"]);
  const { id } = useParams({ from: Route.id });
  const navigate = useNavigate();
  const toast = useToast();

  const { data: task, isLoading, isError } = useTask(id);
  const update = useUpdateTask(id);
  const [errorMessage, setErrorMessage] = useState<string | undefined>();

  if (isLoading) return <PageSkeleton />;

  if (isError || !task) {
    return (
      <div className="p-8">
        <EmptyState
          title={t("errors.notFound", { ns: "tasks" })}
          actionLabel={t("list.title", { ns: "tasks" })}
          onAction={() => void navigate({ to: "/tasks" })}
        />
      </div>
    );
  }

  // Soft-deleted tasks must not be editable — the server will 422 anyway, but
  // guard the UI to avoid misleading the operator.
  if (task.deleted_at) {
    return (
      <div className="p-8">
        <EmptyState
          title={t("errors.softDeleted", { ns: "tasks" })}
          actionLabel={t("list.title", { ns: "tasks" })}
          onAction={() => void navigate({ to: "/tasks/$id", params: { id } })}
        />
      </div>
    );
  }

  // Terminal-status tasks (completed / cancelled) are historical records — the
  // detail page gates every inline editor on the same open/in_progress window,
  // so the full edit form must not render here either. Direct navigation to
  // /tasks/$id/edit for such a task lands on a read-only banner back to detail.
  if (task.status === "completed" || task.status === "cancelled") {
    return (
      <div className="p-8">
        <EmptyState
          title={t("errors.terminalReadOnly", { ns: "tasks" })}
          actionLabel={t("list.title", { ns: "tasks" })}
          onAction={() => void navigate({ to: "/tasks/$id", params: { id } })}
        />
      </div>
    );
  }

  async function handleSubmit(body: TaskCreate | TaskUpdate) {
    setErrorMessage(undefined);
    try {
      await update.mutateAsync(body as TaskUpdate);
      toast.success("tasks:toasts.updated");
      void navigate({ to: "/tasks/$id", params: { id } });
    } catch (err) {
      if (err instanceof ApiError) {
        // Prefer the typed machine-readable `code`; fall back to `message`,
        // which carries the same identifier for branches that don't set `code`.
        setErrorMessage(mapApiError(err.code ?? err.message));
      } else {
        setErrorMessage(t("errors.saveFailed", { ns: "tasks" }));
      }
    }
  }

  return (
    <div className="min-h-full bg-gray-50 p-8 dark:bg-gray-900">
      <div className="space-y-4">
        <Breadcrumbs
          items={[
            { label: t("list.title", { ns: "tasks" }), to: "/tasks" },
            { label: task.title, to: `/tasks/${id}` },
            { label: t("form.editTitle", { ns: "tasks" }) },
          ]}
        />
        <PageHeader title={t("form.editTitle", { ns: "tasks" })} />

        <TaskForm
          existing={task}
          onSubmit={handleSubmit}
          onCancel={() => void navigate({ to: "/tasks/$id", params: { id } })}
          submitting={update.isPending}
          errorMessage={errorMessage}
        />
      </div>
    </div>
  );
}
